"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Star,
  MessageSquare,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  Send,
  Trash2,
} from "lucide-react";
import {
  listReviews,
  submitReview,
  deleteReview,
  getMe,
  type ReviewsResponse,
} from "@/lib/auth-api";

interface ReviewsSectionProps {
  title: string;
}

export function ReviewsSection({ title }: ReviewsSectionProps) {
  const [tab, setTab] = useState<"summary" | "reviews">("summary");
  const [data, setData] = useState<ReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<{ id: number; name: string } | null>(null);

  // 리뷰 작성 폼
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");

  const fetchReviews = useCallback(async () => {
    try {
      setData(await listReviews(title));
    } catch {
      // 조용히 실패
    } finally {
      setLoading(false);
    }
  }, [title]);

  useEffect(() => {
    fetchReviews();
    getMe().then((me) => {
      if (me?.user) setCurrentUser({ id: me.user.id, name: me.user.name });
    });
  }, [fetchReviews]);

  const handleSubmit = async () => {
    if (rating === 0 || content.trim().length < 50) return;
    setSubmitting(true);
    setSubmitMsg("");
    try {
      const res = await submitReview(title, rating, content.trim());
      setSubmitMsg(res.message || "리뷰가 등록되었습니다.");
      setRating(0);
      setContent("");
      fetchReviews();
    } catch (err) {
      setSubmitMsg(err instanceof Error ? err.message : "리뷰 작성에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("리뷰를 삭제하시겠습니까?")) return;
    try {
      await deleteReview(id);
      fetchReviews();
    } catch {
      // 조용히 실패
    }
  };

  if (loading) return null;

  const summary = data?.summary;
  const reviews = data?.reviews || [];
  const total = data?.total || 0;

  // 데이터가 아예 없으면 숨김
  if (!summary && reviews.length === 0) return null;

  return (
    <section className="my-4 overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* 헤더 */}
      <div className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-violet-800 px-6 py-4 text-white">
        <MessageSquare className="h-[18px] w-[18px]" />
        <span className="text-base font-bold">시술 후기</span>
        {total > 0 && <span className="ml-auto text-xs opacity-80">{total}건</span>}
      </div>

      {/* 탭 */}
      <div className="flex border-b border-gray-100">
        <button
          onClick={() => setTab("summary")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            tab === "summary"
              ? "border-b-2 border-violet-600 text-violet-700"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          <Sparkles className="mr-1 inline h-3.5 w-3.5" />
          skinagent 분석
        </button>
        <button
          onClick={() => setTab("reviews")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            tab === "reviews"
              ? "border-b-2 border-violet-600 text-violet-700"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          <MessageSquare className="mr-1 inline h-3.5 w-3.5" />
          사용자 후기 {total > 0 && `(${total})`}
        </button>
      </div>

      <div className="p-6">
        {/* AI 분석 탭 */}
        {tab === "summary" && (
          <div>
            {summary ? (
              <div className="space-y-4">
                {/* 만족도 */}
                {summary.avg_satisfaction > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`h-5 w-5 ${
                            s <= Math.round(summary.avg_satisfaction)
                              ? "fill-amber-400 text-amber-400"
                              : "text-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-base font-bold text-gray-800">
                      {summary.avg_satisfaction.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      ({summary.total_reviews}건 분석)
                    </span>
                  </div>
                )}

                {/* 가격 범위 */}
                {summary.price_range && (
                  <div className="rounded-xl bg-gray-50 px-4 py-3">
                    <span className="text-muted-foreground text-xs">예상 가격대</span>
                    <div className="mt-1 text-sm font-semibold">
                      {summary.price_range.min.toLocaleString()}원 ~{" "}
                      {summary.price_range.max.toLocaleString()}원
                    </div>
                    {summary.price_range.typical && (
                      <span className="text-muted-foreground text-xs">
                        일반적: {summary.price_range.typical}
                      </span>
                    )}
                  </div>
                )}

                {/* 장점 */}
                {summary.pros && summary.pros.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-teal-700">
                      <ThumbsUp className="h-3.5 w-3.5" />
                      장점
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {summary.pros.map((pro, i) => (
                        <span
                          key={i}
                          className="rounded-full bg-teal-50 px-3 py-1 text-xs text-teal-700"
                        >
                          {pro}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 단점 */}
                {summary.cons && summary.cons.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-600">
                      <ThumbsDown className="h-3.5 w-3.5" />
                      단점
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {summary.cons.map((con, i) => (
                        <span
                          key={i}
                          className="rounded-full bg-red-50 px-3 py-1 text-xs text-red-600"
                        >
                          {con}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 팁 */}
                {summary.tip && (
                  <div className="flex items-start gap-2 rounded-xl bg-violet-50 px-4 py-3">
                    <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                    <p className="text-sm text-violet-800">{summary.tip}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground py-8 text-center text-sm">
                아직 분석 데이터가 없습니다
              </p>
            )}
          </div>
        )}

        {/* 사용자 후기 탭 */}
        {tab === "reviews" && (
          <div className="space-y-4">
            {reviews.length > 0 ? (
              <>
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              className={`h-3.5 w-3.5 ${
                                s <= review.rating
                                  ? "fill-amber-400 text-amber-400"
                                  : "text-gray-200"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs font-medium text-gray-700">
                          {review.user_name || "익명"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">
                          {new Date(review.created_at).toLocaleDateString("ko-KR")}
                        </span>
                        {currentUser && currentUser.id === review.user_id && (
                          <button
                            onClick={() => handleDelete(review.id)}
                            className="text-gray-300 transition-colors hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed whitespace-pre-line text-gray-700">
                      {review.content}
                    </p>
                  </div>
                ))}
              </>
            ) : (
              <p className="text-muted-foreground py-8 text-center text-sm">
                아직 후기가 없습니다. 첫 후기를 남겨보세요!
              </p>
            )}

            {/* 리뷰 작성 폼 */}
            {currentUser ? (
              <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/30 p-4">
                <p className="mb-3 text-sm font-semibold text-gray-700">후기 작성</p>

                {/* 별점 */}
                <div className="mb-3 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onMouseEnter={() => setHoverRating(s)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(s)}
                    >
                      <Star
                        className={`h-6 w-6 transition-colors ${
                          s <= (hoverRating || rating)
                            ? "fill-amber-400 text-amber-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="text-muted-foreground ml-2 text-xs">{rating}점</span>
                  )}
                </div>

                {/* 내용 */}
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="시술 경험을 50자 이상 작성해주세요"
                  rows={4}
                  className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-300 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                />
                <div className="mt-1 flex items-center justify-between">
                  <span
                    className={`text-xs ${
                      content.trim().length >= 50 ? "text-teal-600" : "text-muted-foreground"
                    }`}
                  >
                    {content.trim().length}/50자 이상
                  </span>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || rating === 0 || content.trim().length < 50}
                    className="flex items-center gap-1 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {submitting ? "등록 중..." : "등록"}
                  </button>
                </div>
                {submitMsg && <p className="mt-2 text-xs text-violet-700">{submitMsg}</p>}
              </div>
            ) : (
              <div className="mt-4 rounded-xl bg-gray-50 py-4 text-center">
                <p className="text-muted-foreground text-sm">
                  후기를 작성하려면{" "}
                  <a href="/login" className="font-medium text-violet-600 hover:underline">
                    로그인
                  </a>
                  이 필요합니다
                </p>
              </div>
            )}
          </div>
        )}

        {/* 면책 */}
        <p className="mt-4 text-xs text-gray-400">
          개인 경험 기반 통계이며, 의학적 조언이 아닙니다
        </p>
      </div>
    </section>
  );
}
